import {
  selectCurrentContactIdOrTemp,
  selectCurrentContactIsCreating,
  doCreateAndChangeContactForConversation,
} from 'ducks/crm/contacts'
import { doAppGraphqlRequest } from 'ducks/requests'
import { selectCurrentTicketId, selectIsInChat } from 'selectors/location'
import { selectCurrentMatrixUserId } from 'ducks/chat/selectors/users'
import { selectRoomIdFromUrl } from 'ducks/chat/selectors/rooms'
import { selectAccountId } from 'selectors/app/selectAccountId'
import {
  buildRoomAliasFromId,
  getRoomVerifiedIdentityEmail,
} from 'ducks/chat/utils/rooms'
import { getMatrixClient } from 'ducks/chat/utils/client'

import { customFieldValueNodeQuery } from '../../customFields/operations/fetching'
import {
  selectCanPageOnClient,
  selectPageIsLoading,
  selectPageRequestKey,
  selectPageRequestVariables,
  selectRequestsByKey,
  selectContactById,
} from '../selectors'
import { selectCustomFieldIdPages } from '../selectors/customFieldLoadOrder'
import {
  contactsNormalizationSchema,
  conversationContactNormalizationSchema,
} from '../schema'
import {
  FETCH_CONTACT_FAILED,
  FETCH_CONTACT_PAGE,
  FETCH_CONTACT_STARTED,
  FETCH_CONTACT_SUCCESS,
  FETCH_CONTACTS,
  FETCH_CONVERSATION_CONTACT_PAGE,
  FETCH_CONVERSATION_CONTACT_STARTED,
  FETCH_CONVERSATION_CONTACT_SUCCESS,
} from '../types'

const contactCustomFieldValuesSubQuery = `
  customFieldValues(first: 20, after: $after) {
    pageInfo {
      hasNextPage
      endCursor
    }
    ${customFieldValueNodeQuery}
  }
`

const contactCustomFieldValuesSubQueryClientPaging = `
customFieldValues(filter: { customFieldIds: $contactCustomFieldIds }) {
  ${customFieldValueNodeQuery}
}
`

const fetchContactsQuery = `
  query Contacts(
    $first: Int,
    $after: String,
    $filter: ContactFilter,
    $orderBy: ContactOrder
  ) {
    contacts(first: $first, after: $after, filter: $filter, orderBy: $orderBy) {
      totalCount
      totalPageCount
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      nodes {
        id
        companies {
          nodes {
            id
            createdAt
            name
            logoUrl
          }
        }
        contactType
        conversationCount
        createdAt
        email
        lastSeenAt
        name
      }
    }
  }
`

export function doFetchContacts() {
  return (dispatch, getState) => {
    const state = getState()
    const isLoading = selectPageIsLoading(state)
    const requestKey = selectPageRequestKey(state)
    const variables = selectPageRequestVariables(state)

    if (isLoading) return null

    return dispatch(
      doAppGraphqlRequest(FETCH_CONTACTS, fetchContactsQuery, variables, {
        normalizationSchema: contactsNormalizationSchema,
        meta: {
          requestKey,
        },
      })
    )
  }
}

export function doBasicSearchContacts(search) {
  return (dispatch, getState) => {
    const state = getState()
    const requestKey = search
    const isLoading = selectRequestsByKey(state)[requestKey]?.isLoading
    const variables = {
      first: 20,
      filter: { search },
    }

    if (isLoading) return Promise.resolve(null)

    return dispatch(
      doAppGraphqlRequest(FETCH_CONTACTS, fetchContactsQuery, variables, {
        normalizationSchema: contactsNormalizationSchema,
        meta: {
          requestKey,
          mergeEntities: true,
        },
      })
    )
  }
}

const fetchConversationContactCustomFieldsFirstPageQuery = `
  query ConversationContactByConversationNumberFirstPage($after: String, $number: Int!) {
    conversation(number: $number) {
      id
      number
      contact {
        id
        avatarUrl
        companies {
          nodes {
            id
          }
        }
        ${contactCustomFieldValuesSubQuery}
        email
        firstName
        lastName
        lastSeenAt
        createdAt
      }
    }
  }
`

const fetchConversationContactCustomFieldsQuery = `
  query ConversationContactByNumber($after: String, $number: Int!) {
    conversation(number: $number) {
      id
      number
      contact {
        id
        companies {
          nodes {
            id
          }
        }
        ${contactCustomFieldValuesSubQuery}
      }
    }
  }
`

const fetchConversationContactCustomFieldsClientPagingQuery = `
  query ConversationContactByNumber($contactCustomFieldIds: [ID!], $number: Int!) {
    conversation(number: $number) {
      id
      number
      contact {
        id
        ${contactCustomFieldValuesSubQueryClientPaging}
      }
    }
  }
`

const fetchConversationContactCustomFieldsFirstPageClientPagingQuery = `
  query ConversationContactByConversationNumberFirstPage($contactCustomFieldIds: [ID!], $number: Int!) {
    conversation(number: $number) {
      id
      number
      contact {
        id
        avatarUrl
        companies {
          nodes {
            id
          }
        }
        ${contactCustomFieldValuesSubQueryClientPaging}
        email
        firstName
        lastName
        lastSeenAt
      }
    }
  }
`

function makeEntityAvailable(result, contact) {
  // eslint-disable-next-line no-param-reassign
  result[contact.id] = {
    ...contact,
    isPartiallyLoaded: true,
    customFieldValues: contact.customFieldValues || {},
  }
  return result
}

function makeEntitiesAvailable({ contacts, companies }) {
  return {
    companies:
      companies && Object.values(companies).reduce(makeEntityAvailable, {}),
    contacts:
      contacts && Object.values(contacts).reduce(makeEntityAvailable, {}),
  }
}
export function doFetchContactForConversationId(conversationId) {
  return async (dispatch, getState) => {
    const state = getState()
    const contactId = selectCurrentContactIdOrTemp(state)

    let hasNextPage
    let after
    let fetchedContactId
    let page = 0
    const canPageOnClient = selectCanPageOnClient(state)
    const { contact: contactCustomFieldIdPages = [] } = canPageOnClient
      ? selectCustomFieldIdPages(state)
      : {}
    const fetchFirstPageQuery = canPageOnClient
      ? fetchConversationContactCustomFieldsFirstPageClientPagingQuery
      : fetchConversationContactCustomFieldsFirstPageQuery
    const fetchQuery = canPageOnClient
      ? fetchConversationContactCustomFieldsClientPagingQuery
      : fetchConversationContactCustomFieldsQuery
    dispatch({
      type: FETCH_CONVERSATION_CONTACT_STARTED,
      entities: {
        contacts: contactId
          ? {
              [contactId]: {
                id: contactId,
                isLoading: true,
                isCreating: false,
              },
            }
          : {},
      },
      meta: { mergeEntities: true },
    })
    do {
      const variables = {
        after,
        contactCustomFieldIds: contactCustomFieldIdPages[page],
        number: parseInt(conversationId, 10),
      }
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatch(
        doAppGraphqlRequest(
          FETCH_CONVERSATION_CONTACT_PAGE,
          page === 0 ? fetchFirstPageQuery : fetchQuery,
          variables,
          {
            meta: { mergeEntities: true },
            normalizationSchema: conversationContactNormalizationSchema,
            transformEntities: canPageOnClient ? makeEntitiesAvailable : null,
          }
        )
      )
      const { conversation: { contact } = {} } = result || {}
      if (!contact) {
        return dispatch({
          type: FETCH_CONVERSATION_CONTACT_SUCCESS,
          entities: {
            contacts: {
              [contactId]: {
                id: contactId,
                isPartiallyLoaded: true,
                isLoaded: true,
                isMissing: true,
                isLoading: false,
                isCreating: false,
              },
            },
          },
          meta: { mergeEntities: true },
        })
      }
      fetchedContactId = contact.id
      const { customFieldValues } = contact || {}
      const { pageInfo } = customFieldValues || {}
      hasNextPage = pageInfo?.hasNextPage
      after = pageInfo && pageInfo.endCursor
      page += 1
    } while (canPageOnClient ? contactCustomFieldIdPages[page] : hasNextPage)

    dispatch({
      type: FETCH_CONVERSATION_CONTACT_SUCCESS,
      entities: {
        contacts: contactId
          ? {
              [contactId]: {
                id: contactId,
                isPartiallyLoaded: true,
                isLoaded: true,
                isLoading: false,
                isCreating: false,
              },
              [fetchedContactId]: {
                id: fetchedContactId,
                isPartiallyLoaded: true,
                isLoaded: true,
                isLoading: false,
                isCreating: false,
              },
            }
          : {},
      },
      meta: { mergeEntities: true, contactId },
    })
    return fetchedContactId
  }
}

const fetchContactCustomFieldsByCustomFieldFirstPageQuery = `
  query ContactByCustomFieldFirstPage($after: String, $filter: ContactFilter) {
    contacts(filter: $filter, first: 1) {
      nodes {
        id
        avatarUrl
        companies {
          nodes {
            id
          }
        }
        email
        firstName
        lastName
        lastSeenAt
        ${contactCustomFieldValuesSubQuery}
      }
    }
  }
`

const fetchContactCustomFieldsByCustomFieldQuery = `
  query ContactByCustomField($after: String, $filter: ContactFilter) {
    contacts(filter: $filter, first: 1) {
      nodes {
        id
        ${contactCustomFieldValuesSubQuery}
      }
    }
  }
`

export function doFetchContactForCustomField(
  key,
  value,
  conversationId,
  isInChat = false,
  options = {}
) {
  return async (dispatch, getState) => {
    const { allowCreateContact = true, disableSuccessActions = false } = options
    let hasNextPage
    let after
    let firstLoop = true
    const state = getState()
    const contactId = selectCurrentContactIdOrTemp(state)
    const isCreatingContact = selectCurrentContactIsCreating(state)

    const customFields = [
      {
        key,
        values: [value],
      },
    ]
    let fetchedContactId

    dispatch({
      type: FETCH_CONTACT_STARTED,
      entities: {
        [contactId]: {
          customFields,
          isLoading: true,
          isCreating: false,
        },
      },
    })
    let noContactFound = false
    do {
      const variables = {
        after,
        filter: { customFields },
      }
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatch(
        doAppGraphqlRequest(
          FETCH_CONTACT_PAGE,
          firstLoop
            ? fetchContactCustomFieldsByCustomFieldFirstPageQuery
            : fetchContactCustomFieldsByCustomFieldQuery,
          variables,
          {
            meta: {
              mergeEntities: true,
              identifyingKey: key,
              identifyingValue: value,
            },
            normalizationSchema: contactsNormalizationSchema,
          }
        )
      )
      const { contacts: { nodes: [contact] = [] } = {} } = result
      if (!contact) {
        if (!disableSuccessActions) {
          dispatch({
            type: FETCH_CONTACT_SUCCESS,
            entities: {
              contacts: {
                [contactId]: {
                  id: contactId,
                  isPartiallyLoaded: true,
                  isLoaded: true,
                  isLoading: false,
                  isMissing: true,
                  isCreating: false,
                },
              },
            },
            meta: { mergeEntities: true },
          })
        }

        noContactFound = true
        break
      }
      const { customFieldValues } = contact || {}
      const { pageInfo } = customFieldValues || {}

      fetchedContactId = contact.id
      hasNextPage = pageInfo && pageInfo.hasNextPage
      after = pageInfo && pageInfo.endCursor
      firstLoop = false
    } while (hasNextPage)

    if (
      allowCreateContact &&
      noContactFound &&
      !isCreatingContact &&
      isInChat &&
      conversationId &&
      value
    ) {
      // it might be possible to self heal and create this contact
      const client = getMatrixClient()
      const accountId = selectAccountId(state)
      const roomId = selectRoomIdFromUrl(state)
      const roomAlias = buildRoomAliasFromId(roomId, accountId)
      const { room_id: matrixRoomId } = await client.resolveRoomAlias(roomAlias)
      const matrixRoom = client.getRoom(matrixRoomId)
      if (matrixRoom) {
        const userId =
          key === 'contact_matrix_id' && !!value
            ? value
            : selectCurrentMatrixUserId(state)

        // if verfied email exists for this chat, send to create contact
        const verifiedEmail = getRoomVerifiedIdentityEmail(matrixRoom)

        // create contact
        return dispatch(
          doCreateAndChangeContactForConversation(
            contactId,
            `cnv_${conversationId}`,
            conversationId,
            '',
            verifiedEmail,
            userId
          )
        )
      }
    }

    if (fetchedContactId && !disableSuccessActions) {
      try {
        const contact = {
          id: contactId,
          isPartiallyLoaded: true,
          isLoaded: true,
          isLoading: false,
        }
        dispatch({
          type: FETCH_CONTACT_SUCCESS,
          entities: {
            contacts: contactId
              ? {
                  [contactId]: contact,
                  [fetchedContactId]: {
                    ...contact,
                    id: fetchedContactId,
                  },
                }
              : {},
          },
          meta: { mergeEntities: true },
        })
      } catch (error) {
        dispatch({
          type: FETCH_CONTACT_FAILED,
          error: true,
          payload: error,
        })
      }
    }

    return fetchedContactId
  }
}

export const doFetchContactForPrimaryEmail = email => async (
  dispatch,
  getState
) => {
  const contactId = await dispatch(
    doFetchContactForCustomField('contact_email', email)
  )

  return selectContactById(getState(), contactId)
}

export function doFetchCurrentContact(options = {}) {
  return (dispatch, getState) => {
    const state = getState()
    const conversationId = selectCurrentTicketId(state)
    const userId = selectCurrentMatrixUserId(state)
    const isInChat = selectIsInChat(state)

    // Will be added for loading sidebar for contact detail page
    // if (contactId) {
    //   dispatch(doFetchContactForId(contactId))
    // } else
    if (userId) {
      return dispatch(
        doFetchContactForCustomField(
          'contact_matrix_id',
          userId,
          conversationId,
          isInChat,
          options
        )
      )
    } else if (conversationId && !isInChat) {
      return dispatch(doFetchContactForConversationId(conversationId))
    }
    return null
  }
}
